import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Activity, CheckCircle2, ArrowRight, Loader2, Building2, Shield, FileText, Check, Users, Info, AlertTriangle
} from 'lucide-react';

const PRICING_TIERS = [
  { max: 6, price: 35.00, label: 'Até 6 colaboradores' },
  { max: 15, price: 25.00, label: 'Até 15 colaboradores' },
  { max: 30, price: 20.00, label: 'Até 30 colaboradores' },
  { max: 50, price: 15.00, label: 'Até 50 colaboradores' },
  { max: 100, price: 10.00, label: 'Até 100 colaboradores' },
  { max: null, price: null, label: 'Acima de 100 (preço sob consulta)' },
];

const CompanyPlansPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [step, setStep] = useState('plans');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [employeeCount, setEmployeeCount] = useState('');
  const [customPrice, setCustomPrice] = useState('');

  React.useEffect(() => {
    if (!authLoading && !user) navigate('/');
  }, [user, authLoading, navigate]);

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  const count = parseInt(employeeCount) || 0;
  const activeTier = PRICING_TIERS.find(t => t.max === null ? count > 100 : count <= t.max) || PRICING_TIERS[0];
  const pricePerEmployee = activeTier.max === null ? (parseFloat(customPrice) || 0) : activeTier.price;
  const totalMonthly = count * pricePerEmployee;

  const plans = [
    {
      id: 'annual', name: 'Plano Anual', months: 12,
      icon: <Building2 className="w-7 h-7" />, color: 'blue',
      features: [
        'Dashboard completo de saúde', 'Avaliações físicas trimestrais', 'Relatórios de frequência',
        'Alertas de baixa frequência', 'Ranking de engajamento por setor', 'Suporte prioritário'
      ]
    },
    {
      id: 'biennial', name: 'Plano 24 Meses', months: 24, discount: 10, popular: true,
      icon: <Shield className="w-7 h-7" />, color: 'emerald',
      features: [
        'Tudo do Plano Anual', '10% de desconto no valor total', 'Insights de IA avançados',
        'Exportação PDF e Excel', 'API de integração com RH', 'Gerente de conta dedicado'
      ]
    }
  ];

  const handleSelectPlan = (plan) => {
    if (count < 1) {
      toast.error('Informe a quantidade de colaboradores');
      return;
    }
    if (activeTier.max === null && !customPrice) {
      toast.error('Informe o preço por colaborador');
      return;
    }
    setSelectedPlan({ ...plan, employeeCount: count, pricePerEmployee, totalMonthly });
    setStep('terms');
  };

  const handleAcceptAndSubscribe = async () => {
    if (!termsAccepted) {
      toast.error('Você precisa aceitar os termos');
      return;
    }
    setSubmitting(true);
    try {
      // Accept terms first (auditable acceptance)
      await axios.post(`${API}/companies/accept-terms`, {});

      // Create Stripe Checkout session — backend recalculates the total server-side
      const originUrl = window.location.origin;
      const payload = {
        plan_id: selectedPlan.id,
        plan_type: 'company',
        origin_url: originUrl,
        employee_count: selectedPlan.employeeCount,
      };
      if (activeTier.max === null) {
        payload.custom_price_per_employee = parseFloat(customPrice);
      }

      const res = await axios.post(`${API}/payments/create-checkout`, payload);
      if (res.data.url) {
        window.location.href = res.data.url;
        return;
      }
      throw new Error('Sessão de pagamento não retornou URL');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar pagamento');
      setSubmitting(false);
    }
  };

  const colorMap = {
    blue: { bg: 'bg-blue-600', light: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', hover: 'hover:bg-blue-700' },
    emerald: { bg: 'bg-emerald-600', light: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', hover: 'hover:bg-emerald-700' }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-plans-page">
      <Toaster position="top-center" />
      <header className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Activity className="w-7 h-7 text-blue-600" />
          <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Vital</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-16">
        {step === 'plans' && (
          <>
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Assinatura Empresa Parceira
              </h1>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Escolha o plano ideal para transformar a saúde corporativa da sua empresa.
              </p>
            </div>

            {/* Employee Count & Dynamic Pricing */}
            <div className="max-w-2xl mx-auto mb-10 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="employee-pricing">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Quantidade de Colaboradores</h3>
                  <p className="text-sm text-slate-500">Informe para calcular o valor do plano</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-sm text-slate-600 mb-1.5 block">N. de colaboradores</Label>
                  <Input
                    type="number"
                    min="1"
                    data-testid="employee-count-input"
                    value={employeeCount}
                    onChange={(e) => setEmployeeCount(e.target.value)}
                    placeholder="Ex: 25"
                    className="text-lg"
                  />
                </div>
                {activeTier.max === null && count > 100 && (
                  <div>
                    <Label className="text-sm text-slate-600 mb-1.5 block">Preço por colaborador (R$)</Label>
                    <Input
                      type="number"
                      min="1"
                      step="0.01"
                      data-testid="custom-price-input"
                      value={customPrice}
                      onChange={(e) => setCustomPrice(e.target.value)}
                      placeholder="Preço editável"
                      className="text-lg"
                    />
                  </div>
                )}
              </div>

              {/* Pricing Tiers Table */}
              <div className="border border-slate-200 rounded-xl overflow-hidden mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="text-left px-4 py-2.5 font-semibold text-slate-700">Faixa</th>
                      <th className="text-right px-4 py-2.5 font-semibold text-slate-700">Valor/colaborador</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PRICING_TIERS.map((tier, i) => (
                      <tr key={i} className={`border-t border-slate-100 ${activeTier === tier && count > 0 ? 'bg-blue-50' : ''}`}>
                        <td className="px-4 py-2.5 text-slate-700">{tier.label}</td>
                        <td className="px-4 py-2.5 text-right font-medium text-slate-900">
                          {tier.price !== null ? `R$ ${tier.price.toFixed(2)}` : 'Sob consulta'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {count > 0 && (
                <div className="bg-blue-50 rounded-xl p-4 flex items-center justify-between" data-testid="pricing-summary">
                  <div>
                    <div className="text-sm text-blue-700">{count} colaboradores x R$ {pricePerEmployee.toFixed(2)}</div>
                    <div className="text-xs text-blue-600 mt-0.5">Faixa: {activeTier.label}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-blue-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      R$ {totalMonthly.toFixed(2)}
                    </div>
                    <div className="text-xs text-blue-600">/mês</div>
                  </div>
                </div>
              )}

              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-testid="billing-notice">
                <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800">
                  <strong>Importante:</strong> A cobrança será realizada posteriormente com base na quantidade real de colaboradores ativos no período. O valor acima é uma estimativa para fins de planejamento.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              {plans.map(plan => {
                const c = colorMap[plan.color];
                const discountMultiplier = plan.discount ? (1 - plan.discount / 100) : 1;
                const planTotal = totalMonthly * plan.months * discountMultiplier;
                return (
                  <div key={plan.id} className={`bg-white rounded-2xl border ${plan.popular ? 'border-emerald-300 ring-2 ring-emerald-100' : 'border-slate-200'} p-8 relative`}>
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                        10% DESCONTO
                      </div>
                    )}
                    <div className={`w-14 h-14 ${c.light} rounded-2xl flex items-center justify-center ${c.text} mb-4`}>
                      {plan.icon}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{plan.name}</h3>

                    {count > 0 ? (
                      <div className="mb-1">
                        <span className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                          R$ {planTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span className="text-sm text-slate-500 ml-1">total</span>
                        <p className="text-sm text-slate-500">ou {plan.months}x de R$ {(planTotal / plan.months).toFixed(2)}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 mb-2">Informe o n. de colaboradores</p>
                    )}

                    <ul className="space-y-2.5 mb-8 mt-4">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${c.text}`} />
                          {f}
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => handleSelectPlan(plan)}
                      data-testid={`plan-${plan.id}`}
                      disabled={count < 1}
                      className={`w-full ${c.bg} ${c.hover} text-white`}
                    >
                      Assinar {plan.name}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {step === 'terms' && selectedPlan && (
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <FileText className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Termos de Uso e Assinatura
              </h1>
              <p className="text-slate-600">Plano: <strong>{selectedPlan.name}</strong> | {selectedPlan.employeeCount} colaboradores | R$ {selectedPlan.totalMonthly.toFixed(2)}/mês</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 max-h-96 overflow-y-auto text-sm text-slate-600 leading-relaxed space-y-4">
              <h3 className="font-bold text-slate-900 text-base">TERMO DE USO E CONDIÇÕES GERAIS DA PLATAFORMA VITAL PERFORMANCE</h3>

              <p><strong>1. ACEITAÇÃO DOS TERMOS</strong><br/>Ao acessar, se cadastrar ou utilizar o sistema digital da Vital Performance, o usuário declara ter lido, compreendido e aceito integralmente os presentes Termos de Uso e Condições Gerais, bem como a Política de Privacidade aplicável. O uso da plataforma está condicionado à aceitação total destas condições.</p>

              <p><strong>2. CADASTRO E ACESSO À PLATAFORMA</strong><br/>2.1. Para utilização dos serviços, o usuário deverá realizar cadastro com informações verdadeiras, completas e atualizadas. 2.2. O usuário é o único responsável pela veracidade dos dados fornecidos, bem como pela guarda e sigilo de suas credenciais de acesso. 2.3. É vedado: Criar contas com dados falsos ou de terceiros sem autorização; Compartilhar login e senha com terceiros; Utilizar a plataforma para fins ilícitos ou não autorizados. 2.4. A Vital Performance se reserva o direito de suspender ou cancelar contas que violem estes termos, sem aviso prévio.</p>

              <p><strong>3. REGRAS DE UTILIZAÇÃO</strong><br/>3.1. O usuário compromete-se a utilizar a plataforma de forma ética, respeitando a legislação vigente e os princípios de boa-fé. 3.2. É proibido: Praticar qualquer tentativa de invasão, engenharia reversa ou exploração do sistema; Utilizar o sistema para prejudicar outros usuários ou parceiros; Inserir conteúdos ofensivos, ilegais ou que violem direitos de terceiros.</p>

              <p><strong>4. MODELO DE COBRANÇA E REGULAMENTAÇÃO DE VALORES</strong><br/>4.1. A Vital Performance atua como plataforma intermediadora de serviços, podendo definir, estruturar e regulamentar os modelos de cobrança aplicáveis. 4.2. Os valores cobrados na plataforma poderão incluir: Assinaturas mensais; Taxas de uso; Comissões sobre transações; Planos recorrentes ou personalizados. 4.3. A Vital Performance se reserva o direito de: Alterar valores, planos e modelos de monetização a qualquer momento; Criar campanhas promocionais com regras específicas; Definir critérios de elegibilidade para determinados serviços. 4.4. Todas as alterações serão comunicadas previamente dentro da plataforma ou por canais oficiais.</p>

              <p><strong>Dados da contratação:</strong><br/>Plano selecionado: {selectedPlan.name}. Cobrança baseada em {selectedPlan.employeeCount} colaboradores a R$ {selectedPlan.pricePerEmployee.toFixed(2)}/colaborador, totalizando R$ {selectedPlan.totalMonthly.toFixed(2)}/mês. Vigência de {selectedPlan.months} meses. A cobrança será realizada posteriormente com base na quantidade real de colaboradores ativos.</p>

              <p><strong>5. PAGAMENTOS E RESPONSABILIDADES FINANCEIRAS</strong><br/>5.1. O usuário concorda em cumprir integralmente com as obrigações financeiras assumidas no momento da contratação. 5.2. Em caso de inadimplência, a plataforma poderá: Suspender o acesso; Cancelar serviços ativos; Aplicar encargos conforme legislação vigente.</p>

              <p><strong>6. PRIVACIDADE E PROTEÇÃO DE DADOS</strong><br/>6.1. A Vital Performance compromete-se com a proteção dos dados pessoais dos usuários, em conformidade com a LGPD. 6.2. Os dados coletados são utilizados para: Operação da plataforma; Personalização da experiência do usuário; Comunicação e suporte; Cumprimento de obrigações legais. 6.3. A plataforma adota medidas técnicas e administrativas para garantir a segurança das informações. 6.4. O usuário tem direito a: Acessar seus dados; Corrigir informações; Solicitar exclusão, quando aplicável.</p>

              <p><strong>7. PROPRIEDADE INTELECTUAL</strong><br/>7.1. Todo o conteúdo, tecnologia, design e funcionalidades da plataforma são de propriedade exclusiva da Vital Performance. 7.2. É proibida a reprodução, cópia ou uso indevido sem autorização expressa.</p>

              <p><strong>8. LIMITAÇÃO DE RESPONSABILIDADE</strong><br/>8.1. A Vital Performance não se responsabiliza por: Falhas decorrentes de uso inadequado da plataforma; Problemas técnicos externos; Interrupções não previstas por fatores fora do seu controle.</p>

              <p><strong>9. ALTERAÇÕES DOS TERMOS</strong><br/>9.1. A Vital Performance poderá atualizar estes Termos a qualquer momento, visando melhoria contínua do serviço. 9.2. O uso contínuo da plataforma após alterações implica concordância com os novos termos.</p>

              <p><strong>10. DISPOSIÇÕES GERAIS</strong><br/>10.1. Estes Termos constituem o acordo integral entre as partes. 10.2. Fica eleito o foro da comarca do domicílio da empresa para dirimir quaisquer conflitos oriundos destes termos.</p>

              <p className="text-center font-semibold text-slate-800 pt-2 border-t border-slate-200">
                SAULO PAIXÃO FERNANDES<br />
                <span className="font-normal text-slate-500">CEO VITAL PERFORMANCE</span>
              </p>
            </div>

            <div className="flex items-start gap-3 mb-6 p-4 bg-blue-50 rounded-xl">
              <Checkbox
                id="terms-checkbox"
                data-testid="terms-checkbox"
                checked={termsAccepted}
                onCheckedChange={setTermsAccepted}
              />
              <label htmlFor="terms-checkbox" className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                Declaro que li e aceito os <strong>Termos de Uso</strong> e estou ciente das condições de pagamento.
              </label>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setStep('plans'); setTermsAccepted(false); }} className="flex-1">
                Voltar
              </Button>
              <Button
                onClick={handleAcceptAndSubscribe}
                disabled={!termsAccepted || submitting}
                data-testid="accept-terms-btn"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Eu Aceito os Termos
              </Button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Assinatura Confirmada!
            </h1>
            <p className="text-slate-600 mb-4">Redirecionando para o dashboard...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyPlansPage;
